import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  Input,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { DescriptionRounded } from "@mui/icons-material";
import PageHeader from "../components/page-header";
import { Dialogs } from "../components/dialogs";
import StickyHeadTable, { Column } from "../components/table";
import { Exame } from "../types/exames";
import tasks from "../utils/tasks";
import { ViewModelExame } from "../viewmodel/viewmodel-exame";

function ExamesPage() {

  const viewModel = ViewModelExame();
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [viewMode, setViewMode] = useState<"all" | "suggestions">("all");

  // Form state
  const [title, setTitle] = useState("");
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [file, setFile] = useState<any>(null);
  const [data, setData] = useState("");
  const [visible, setVisible] = useState<boolean>(true);
  const [author, setAuthor] = useState("");
  const [schoolId, setSchoolId] = useState<number>(0);

  const handleOpen = () => {
    setEditMode(false);
    setTitle("");
    setYear(new Date().getFullYear());
    setFile(null);
    setData("");
    setVisible(true);
    setAuthor("");
    if (viewModel.schoolIdParam) {
      setSchoolId(Number(viewModel.schoolIdParam));
    }
    setOpen(true);
  };

  const handleEdit = (id: number) => {
    const exame = viewModel.exames.data.find((p) => p.id === id);
    if (exame) {
      setEditMode(true);
      setTitle(exame.title);
      setYear(exame.year);
      setFile(exame.url);
      setData(exame.data || "");
      setVisible(exame.visible ?? true);
      setAuthor(exame.author || "");
      setSchoolId(exame.school_id);
      viewModel.selectExame(exame);
      setOpen(true);
    }
  };

  const handleFile = (event: any) => {
    const file = event.target.files[0];
    if (file) {
      setFile(file);
    }
  };

  const validateForm = (): string | null => {

    if (!schoolId) {
      return "Selecione uma secção";
    }
    if (!title.trim()) {
      return "O título é obrigatório";
    }
    if (!year || year < 1900 || year > 2100) {
      return "Ano inválido";
    }
    if (!editMode && !file) {
      return "Por favor, selecione um ficheiro.";
    }
    return null;
  };

  const handleSubmit = async () => {
    const error = validateForm();
    if (error) {
      viewModel.showWarning({
        title: "Erro",
        message: error,
      });
      return;
    }

    setOpen(false);
    const exameData = {
      title,
      year,
      url: file,
      data,
      visible,
      author,
      school_id: schoolId,
    };

    editMode && viewModel.selectedExame?.id
      ? viewModel.handleActionExame(
        { id: viewModel.selectedExame.id, ...exameData },
        "update"
      )
      : viewModel.handleActionExame(exameData, "add");
  };

  const handleDelete = (id: number) => {
    const exame = viewModel.exames.data.find((p) => p.id === id);
    viewModel.showConfirmation({
      title: "Confirmar Eliminação",
      message: `Tem a certeza que deseja eliminar o exame "${exame?.title}"? Esta ação não pode ser desfeita.`,
      onConfirm: () => viewModel.handleDeleteExame(id),
    });
  };

  const handleValidate = (id: number) => {
    const exame = viewModel.exames.data.find((p) => p.id === id);
    viewModel.showConfirmation({
      title: "Confirmar Validação",
      message: `Tem a certeza que deseja validar o exame "${exame?.title}"?`,
      onConfirm: () => viewModel.handleValidateExame(id),
    });
  };

  const handleInvalidate = (id: number) => {
    const exame = viewModel.exames.data.find((p) => p.id === id);
    viewModel.showConfirmation({
      title: "Confirmar Invalidação",
      message: `Tem a certeza que deseja invalidar o exame "${exame?.title}"?`,
      onConfirm: () => viewModel.handleRejectExame(id),
    });
  };

  const columns: Column<Exame>[] = [
    { id: "id", label: "ID", minWidth: 80 },
    { id: "title", label: "Título", minWidth: 300 },
    {
      id: "year",
      label: "Ano",
      minWidth: 100,
    },
    {
      id: "author",
      label: "Autor",
      minWidth: 200,
      renderCell: (row) => row.author || "Sem Info."
    },
    {
      id: "school_id",
      label: "Secção",
      minWidth: 150,
      renderCell: (row) => {
        const school = viewModel.schools.data.find((s) => s.id === row.school_id);
        return school?.name || "Sem Info."
      }
    },
    {
      id: "visible",
      label: "Visível",
      minWidth: 80,
      renderCell: (row) => (
        <Chip
          label={row.visible ? "Sim" : "Não"}
          color={row.visible ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      id: "actions",
      label: "Ações",
      minWidth: 150,
      renderCell: (row) => (
        <Box>
          <Tooltip title="Editar" arrow>
            <IconButton
              size="small"
              onClick={() => handleEdit(row.id!)}
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {row.visible ? (
            <Tooltip title="Invalidar" arrow>
              <IconButton
                size="small"
                color="warning"
                onClick={() => handleInvalidate(row.id!)}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Validar" arrow>
              <IconButton
                size="small"
                color="success"
                onClick={() => handleValidate(row.id!)}
              >
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Eliminar" arrow>
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(row.id!)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const tableData = useMemo(() => {
    return viewModel.exames.data.map((item) => {
      return {
        ...item,
        author: item.author || "Sem Info.",
      }
    })
  }, [viewModel.exames]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <PageHeader
        icon={<DescriptionRounded />}
        color="#EF4444"
        title="Gestão de Exames"
        subtitle={viewModel.schoolIdParam ? `Filtrado por secção ID: ${viewModel.schoolIdParam}` : undefined}
        onBack={viewModel.schoolIdParam ? viewModel.goToSchools : () => viewModel.navigate("/painel")}
        actions={
          <>
            <Button
              variant={viewMode === "all" ? "contained" : "outlined"}
              onClick={() => {
                setViewMode("all");
                if (viewModel.schoolIdParam) {
                  viewModel.fetchExamesBySchool(Number(viewModel.schoolIdParam));
                } else {
                  viewModel.fetchExames();
                }
              }}
            >
              Todas
            </Button>
            <Button
              variant={viewMode === "suggestions" ? "contained" : "outlined"}
              startIcon={<LightbulbIcon />}
              onClick={() => {
                setViewMode("suggestions");
                viewModel.fetchSuggestions();
              }}
            >
              Sugestões
            </Button>
            <Button variant="contained" onClick={handleOpen}>
              Novo Exame
            </Button>
          </>
        }
      />

      <StickyHeadTable
        columns={columns}
        rows={tableData}
        getRowId={(row) => row.id!}
        pagination={{
          totalData: viewModel.exames.pagination.totalData,
          totalPages: viewModel.exames.pagination.totalPages,
          currentPage: viewModel.exames.pagination.currentPage,
          onPageChange: (newPage) => {
            window.scrollTo({ top: 0, behavior: "smooth" });
            if (viewModel.schoolIdParam) {
              const schoolIdNum = Number(viewModel.schoolIdParam);
              viewModel.fetchExamesBySchool(schoolIdNum, newPage);
            } else {
              viewModel.fetchExames(newPage);
            }
          },
          onRowsPerPageChange: () => { },
        }}
      />

      {/* Create/Edit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editMode ? "Editar Exame" : "Novo Exame"}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Secção</InputLabel>
            <Select
              value={schoolId}
              label="Secção"
              onChange={(e) => setSchoolId(Number(e.target.value))}
              disabled={!editMode && !!viewModel.schoolIdParam}
            >
              {viewModel.schools.data.map((school) => (
                <MenuItem key={school.id} value={school.id}>
                  {school.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            autoFocus
            margin="normal"
            label="Título"
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <TextField
            margin="normal"
            label="Ano"
            type="number"
            fullWidth
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            inputProps={{ min: 1900, max: 2100 }}
          />

          <TextField
            margin="normal"
            label="Autor"
            fullWidth
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
          />

          <TextField
            margin="normal"
            label="Data (adicional)"
            fullWidth
            value={data}
            onChange={(e) => setData(e.target.value)}
          />

          {
            editMode ?
              (<Box sx={{ mt: 2 }}>
                <a href={tasks.generateFileUrl(file)} download style={{ textDecoration: 'none' }}>
                  <Button
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    fullWidth
                  >
                    Baixar Ficheiro
                  </Button>
                </a>
              </Box>) :
              (<Box sx={{ mt: 2 }}>
                <Input
                  id="file-input"
                  type="file"
                  style={{ display: 'none' }}
                  onChange={handleFile}
                />
                <label htmlFor="file-input">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadFileIcon />}
                    fullWidth
                  >
                    {file ? file.name : "Selecionar Ficheiro"}
                  </Button>
                </label>
                {file && (
                  <Typography variant="caption" display="block" sx={{ mt: 1, color: 'success.main' }}>
                    ✓ Ficheiro selecionado: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                  </Typography>
                )}
              </Box>)
          }

        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? "Actualizar" : "Criar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialogs {...viewModel.dialog} />
    </Container>
  );
}

export default ExamesPage;
